import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowLeft,
  faInfoCircle,
  faFile,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import Confirm from "../../molecules/Confirm";
import Alerts from "../../molecules/Alerts";
import { trackPromise } from "react-promise-tracker";
import { v4 as UUID } from "uuid";
import {
  apiGetRequest,
  apiDeleteFile,
  apiPostFiles,
} from "../../../utils/queries";
import { Validate } from "../../../utils/Validator";
import config from "../../../config";
import "./FilesPage.css";
import { useModalCleanup } from "../../../utils/useModalCleanup";

const FilesPage = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const modalCleanup = useModalCleanup();

  const [account, setAccount] = useState({});
  const [bankDataList, setBankDataList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [accountLoaded, setAccountLoaded] = useState(false);
  const [toggleConfirmFileDelete, setToggleConfirmFileDelete] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [selectedBankDataId, setSelectedBankDataId] = useState(0);
  const [alerts, setAlerts] = useState([]);


  useEffect(() => {
    const getRequest = async () => {
      try {
        const request = await trackPromise(apiGetRequest(token));

        setAccount(request.data.request || {});
        const bankData = request.data.bankDataList || [];
        setBankDataList(bankData);
        setFileList(request.data.files || request.data.fileList);

        // Set first bank account as default selection
        if (bankData.length > 0) {
          setSelectedBankDataId(bankData[0].id);
        }

        setAccountLoaded(true);
      } catch (error) {
        addAlert("Error loading account data. Please try again. " + error, "error");
      }
    };

    if (token) {
      getRequest();
    }
  }, [token]);

  // File handling functions
  const onDeleteFile = async (file) => {
    setSelectedFile(file);
    setToggleConfirmFileDelete(!toggleConfirmFileDelete);
  };

  const ALLOWED_EXTENSIONS = new Set([".pdf", ".jpg", ".jpeg", ".png"]);

  const selectedFilesChanged = async (e) => {
    const files = Array.from(e.target.files);

    // Validate file types and sizes before uploading
    for (const file of files) {
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      if (!ALLOWED_EXTENSIONS.has(ext)) {
        addAlert(`"${file.name}" is not an accepted file type. Only PDF, JPG, JPEG and PNG are allowed.`, "error");
        e.target.value = null;
        return;
      }
      if (file.size > config.MAX_ATTACHMENT_SIZE) {
        addAlert(`"${file.name}" exceeds the maximum file size of 5 MB.`, "error");
        e.target.value = null;
        return;
      }
    }

    // If "All Bank Accounts" is selected, upload files to each bank account
    if (selectedBankDataId === -1) {
      try {
        const filesForAllBanks = Validate.filesForAllBanks(files, fileList, bankDataList, showBankDataDetails);
        if (!filesForAllBanks.isValid) {
          addAlert(filesForAllBanks.message, "error");
          return;
        }

        const uploadPromises = bankDataList.map(async (bankData) => {
          const form = new FormData();
          form.append("BankDataId", bankData.id);
          form.append("RequestId", account.id);

          for (const file of files) {
            form.append("file", file);
          }

          return trackPromise(apiPostFiles(form, token));
        });

        const results = await Promise.all(uploadPromises);

        // Flatten all uploaded files and add to file list
        const allUploadedFiles = results.flatMap(result => result?.data || []);
        setFileList([...fileList, ...allUploadedFiles]);

        const successMessage = Validate.fileUploadSuccess(selectedBankDataId, null, true, bankDataList.length);
        addAlert(successMessage, "success");

      } catch (error) {
        const errorMessage = Validate.fileUploadError(error, selectedBankDataId, true);
        addAlert(errorMessage, "error");
      }
      return;
    }

    // Single bank account or "Other" selected
    let form = new FormData();
    form.append("BankDataId", selectedBankDataId);
    form.append("RequestId", account.id);

    for (var index = 0; index < e.target.files.length; index++) {
      var file = e.target.files[index];

      const bankDataDescription = selectedBankDataId === 0
        ? "other documents"
        : showBankDataDetails(selectedBankDataId);

      const isFileDuplicate = Validate.duplicateFileName(file.name, selectedBankDataId, fileList, bankDataDescription);
      if (!isFileDuplicate.isValid) {
        addAlert(isFileDuplicate.message, "error");
        return;
      }

      form.append("file", file);
    }

    try {
      let files = await trackPromise(apiPostFiles(form, token));
      setFileList([...fileList, ...files?.data]);

      const bankDataDescription = selectedBankDataId === 0
        ? "other documents"
        : showBankDataDetails(selectedBankDataId);

      const successMessage = Validate.fileUploadSuccess(selectedBankDataId, bankDataDescription, false, 0);
      addAlert(successMessage, "success");
    } catch (error) {
      const errorMessage = Validate.fileUploadError(error, selectedBankDataId, false);
      addAlert(errorMessage, "error");
    }
  };

  const onDeleteFileConfirmed = async () => {
    try {
      await trackPromise(apiDeleteFile(token, selectedFile.fileName));
      addAlert("File deleted", "success");
      setFileList(
        fileList.filter((item) => item.fileName !== selectedFile.fileName)
      );
    } catch (error) {
      addAlert("Could not delete file. Try again. " + error, "error");
    }
  };

  const addAlert = (message, color, timeout = 7500) => {
    let id = UUID();
    setAlerts((prev) => [
      ...prev,
      { message: message, id: id, color: color, timeout: timeout },
    ]);
  };

  /**
   * Used in the bank data selection dropdown to show details about each bank data
   * @param {*} bankDataId 
   */
  const showBankDataDetails = (bankDataId) => {
    const bankData = bankDataList.find(bd => bd.id === bankDataId);
    if (!bankData) return `Bank Account #${bankDataId}`;

    if (bankData.type === "REGNO/ACCOUNT") {
      return `${bankData.type}${bankData.bankAccountNumber ? ` - ${bankData.bankAccountNumber}` : ''}`;
    }

    if (bankData.type === "FIK") {
      return `${bankData.type}${bankData.fikNumber ? ` - ${bankData.fikNumber}` : ''}`;
    }

    return `${bankData.type && `${bankData.type} - `}${bankData.bank || `Bank Account #${bankData.id}`}${bankData.currency?.name && ` (${bankData.currency.name})`}`;
  };

  const formatFileDate = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${mins}`;
  };

  const getFilesForBankData = (bankDataId) => {
    return fileList?.filter((file) => file?.bankDataId === bankDataId) || [];
  };

  const handleNext = () => {
    // Use Validator for required files validation
    const requiredFiles = Validate.anyFiles(fileList);
    if (!requiredFiles.isValid) {
      addAlert(requiredFiles.message, "error");
      return;
    }
    navigate(`/${token}/review`);
  };

  const handleBack = () => {
    navigate(`/${token}/bank`);
  };

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <Alerts alerts={alerts} setAlerts={setAlerts} />
          <div className="vfp-header">
            <div className="vfp-header__step">Step 3 of 4</div>
            <h4 className="vfp-header__title">Upload Files</h4>
            <p className="vfp-header__sub">Provide supporting documentation (invoice or bank letter) that verifies your company name and banking details.</p>
          </div>
          <div className="vfp-body">

              {accountLoaded && (
                <>
                  <div className="files-upload">

                    {/* Bank / category selector */}
                    <div className="files-upload__field">
                      <label className="files-upload__label">Bank Detail *</label>
                      <div className="files-bank-options">
                        {bankDataList.map((bankData) => (
                          <button
                            key={bankData.id}
                            type="button"
                            className={`files-bank-option${selectedBankDataId === bankData.id ? ' files-bank-option--active' : ''}`}
                            onClick={() => setSelectedBankDataId(bankData.id)}
                          >
                            <span className="files-bank-option__name">
                              {bankData.bank || `Bank Account #${bankData.id}`}
                            </span>
                            <span className="files-bank-option__meta">
                              {bankData.type && (
                                <span className="files-bank-option__chip">{bankData.type}</span>
                              )}
                              {bankData.currency?.name && (
                                <span className="files-bank-option__chip">{bankData.currency.name}</span>
                              )}
                            </span>
                          </button>
                        ))}
                        {bankDataList.length > 1 && (
                          <button
                            type="button"
                            className={`files-bank-option${selectedBankDataId === -1 ? ' files-bank-option--active' : ''}`}
                            onClick={() => setSelectedBankDataId(-1)}
                          >
                            <span className="files-bank-option__name">All bank accounts</span>
                            <span className="files-bank-option__meta">
                              <span className="files-bank-option__chip">Upload to all</span>
                            </span>
                          </button>
                        )}
                        <button
                          type="button"
                          className={`files-bank-option${selectedBankDataId === 0 ? ' files-bank-option--active' : ''}`}
                          onClick={() => setSelectedBankDataId(0)}
                        >
                          <span className="files-bank-option__name">Other</span>
                          <span className="files-bank-option__meta">
                            <span className="files-bank-option__chip">Not linked to a bank detail</span>
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Files for selected bank */}
                    {getFilesForBankData(selectedBankDataId).length > 0 && (
                      <div className="rd-file-list">
                        {getFilesForBankData(selectedBankDataId).map((file, index) => (
                          <div key={index} className="rd-file-item">
                            <div className="rd-file-item__icon">
                              <FontAwesomeIcon icon={faFile} />
                            </div>
                            <div className="rd-file-item__info">
                              <a
                                href={file?.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rd-file-item__name"
                              >
                                {file?.displayName}
                              </a>
                              <span className="rd-file-item__meta">
                                {selectedBankDataId === 0 ? 'Other' : showBankDataDetails(selectedBankDataId)}
                                {file?.inserted && ` · ${formatFileDate(file.inserted)}`}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="rd-file-item__delete"
                              onClick={() => onDeleteFile(file)}
                              title="Delete"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add File dropzone */}
                    <label htmlFor="file-upload-mainFileForm" className="files-dropzone">
                      <span className="files-dropzone__icon">+</span>
                      <span className="files-dropzone__text">Add File</span>
                      <span className="files-dropzone__hint">PDF, JPG, JPEG, PNG · max 5 MB per file</span>
                    </label>
                    <input
                      id="file-upload-mainFileForm"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      onChange={selectedFilesChanged}
                    />

                  </div>

                  {/* Confirm Delete Dialog */}
                  <Confirm
                    onConfirm={onDeleteFileConfirmed}
                    text={`Are you sure you want to delete "${selectedFile.displayName}"?`}
                    toggle={toggleConfirmFileDelete}
                    setToggle={setToggleConfirmFileDelete}
                  />
                </>
              )}

              <div className="mt-4 d-flex justify-content-between">
                <Button className="btn-back" onClick={handleBack}>
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                  Back: Bank Details
                </Button>
                <Button className="btn-next" onClick={handleNext}>
                  Next: Review & Submit
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </Button>
              </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default FilesPage;
